import type { UseQueryResult, DefaultError } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../clients';
import { queryKeys } from '../../lib/query-keys';
import { isDirectApiMode } from '../../utils/api-paths';
import { isEmbeddedInStudentHost } from '../../utils/cp-student-context';
import {
  getStudentCurrentHighschoolPath,
  getStudentHighschoolsPath,
  getStudentPlansOfStudyByDistrictOrSchoolsPath,
  getStudentPlansOfStudyByActiveSchoolPath,
} from '../../utils/student/student-api-paths';
import type { School } from '../../types';

export interface StudentSchoolPlanOfStudySummary {
  id: string;
  name: string;
  description?: string;
  descriptionPlainText?: string;
  activeSchools?: StudentSchoolPlanActiveSchool[];
  totalCredits?: number;
  credits?: number;
  requiredCredits?: number;
  requirements?: StudentSchoolPlanRequirement[];
}

export interface StudentSchoolPlanActiveSchool {
  id?: string;
  nid?: string;
  name?: string;
}

export interface StudentSchoolPlanRequirement {
  id?: string;
  name?: string;
  credits?: number;
  description?: string;
  descriptionPlainText?: string;
}

interface StudentSchoolPlansResponseEnvelope {
  total: number;
  data: StudentSchoolPlanOfStudySummary[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

interface CurrentStudentSchoolResponse {
  nid?: unknown;
  name?: unknown;
  alias?: unknown;
}

function getCurrentStudentSchool(response: unknown): School {
  if (typeof response !== 'object' || response === null) {
    throw new TypeError('Current school response must be an object.');
  }

  const { nid, name, alias } = response as CurrentStudentSchoolResponse;
  if (typeof nid !== 'string' || nid.length === 0) {
    throw new TypeError('Current school response must include a non-empty string nid.');
  }

  const displayName =
    typeof name === 'string' && name.length > 0
      ? name
      : typeof alias === 'string' && alias.length > 0
        ? alias
        : nid;

  return { nid, name: displayName };
}

const MOCK_SCHOOLS: School[] = [
  { nid: 'school-1', name: 'Lincoln High School' },
  { nid: 'school-2', name: 'Washington High School' },
  { nid: '88881USPU', name: 'Jefferson Academy' },
  { nid: 'school-4', name: 'Roosevelt High School' },
  { nid: 'school-5', name: 'Kennedy High School' },
];

const MOCK_STUDENT_PLANS_BY_ACTIVE_SCHOOL: StudentSchoolPlansResponseEnvelope = {
  total: 4,
  data: [
    {
      id: '1d52ec3a-7244-4b4a-ae71-316c71194b8a',
      name: 'Advanced College Prep',
      description: `Rigorous academic track with AP and honors courses for competitive college admissions.\nFocuses on a strong foundation in math, science, and humanities.\nIncludes pathways that support selective university applications.`,
      descriptionPlainText:
        'Rigorous academic track with AP and honors courses for competitive college admissions. Focuses on a strong foundation in math, science, and humanities. Includes pathways that support selective university applications.',
      totalCredits: 10,
      requirements: [
        {
          id: 'acp-req-1',
          name: 'English',
          credits: 4,
          descriptionPlainText: 'College-preparatory writing and literature sequence.',
        },
        {
          id: 'acp-req-2',
          name: 'Mathematics',
          credits: 4,
          descriptionPlainText: 'Progressive math sequence through advanced coursework.',
        },
        {
          id: 'acp-req-3',
          name: 'Science',
          credits: 3,
          descriptionPlainText: 'Lab-based science with advanced elective opportunities.',
        },
      ],
    },
    {
      id: '25d138e5-133d-45cf-80d2-f6af238d4595',
      name: 'POS publish-and-update test 1778665110-1',
      description: `Created via publish-and-update test 1778665110.\nUsed for validation of publish and update flows.\nIncludes baseline requirement rows for regression checks.`,
      descriptionPlainText:
        'Created via publish-and-update test 1778665110. Used for validation of publish and update flows. Includes baseline requirement rows for regression checks.',
      totalCredits: 8,
      requirements: [
        {
          id: 'pa1-req-1',
          name: 'Core Science',
          credits: 2,
          descriptionPlainText: 'Two science credits from approved course options.',
        },
        {
          id: 'pa1-req-2',
          name: 'Core Math',
          credits: 2,
          descriptionPlainText: 'Two math credits from approved course options.',
        },
        {
          id: 'pa1-req-3',
          name: 'Electives',
          credits: 4,
          descriptionPlainText: 'Additional elective credits to complete the plan.',
        },
      ],
    },
    {
      id: '389485fa-fd3d-4261-a4ff-63c21c9a9768',
      name: 'POS publish-and-update test 1778665110-2',
      description: `Created via publish-and-update test 1778665110.\nVariant plan used to compare publish-and-update outcomes.\nContains requirement data for end-to-end modal rendering checks.`,
      descriptionPlainText:
        'Created via publish-and-update test 1778665110. Variant plan used to compare publish-and-update outcomes. Contains requirement data for end-to-end modal rendering checks.',
      totalCredits: 8,
      requirements: [
        {
          id: 'pa2-req-1',
          name: 'Science Choice',
          credits: 2,
          descriptionPlainText: 'Select science courses totaling two credits.',
        },
        {
          id: 'pa2-req-2',
          name: 'Math Choice',
          credits: 2,
          descriptionPlainText: 'Select math courses totaling two credits.',
        },
        {
          id: 'pa2-req-3',
          name: 'General Electives',
          credits: 4,
          descriptionPlainText: 'Elective pool supporting schedule flexibility.',
        },
      ],
    },
    {
      id: '3b1d9cfa-bfbe-42ce-8643-60a3fc27cea6',
      name: 'QA_POS',
      description: `QA validation plan for requirement rendering and selection behavior.\nCovers multiple requirement categories and credit totals.\nDesigned to verify consistency across environments.`,
      descriptionPlainText:
        'QA validation plan for requirement rendering and selection behavior. Covers multiple requirement categories and credit totals. Designed to verify consistency across environments.',
      totalCredits: 12,
      requirements: [
        {
          id: 'qa-req-1',
          name: 'Maths',
          credits: 4,
          descriptionPlainText: 'Math-focused requirement set for QA checks.',
        },
        {
          id: 'qa-req-2',
          name: 'Choice',
          credits: 2,
          descriptionPlainText: 'Choice-based requirement for list-selection validation.',
        },
        {
          id: 'qa-req-3',
          name: 'Group',
          credits: 3,
          descriptionPlainText: 'Grouped requirements for composite rendering tests.',
        },
        {
          id: 'qa-req-4',
          name: 'Mandated',
          credits: 3,
          descriptionPlainText: 'Required courses that cannot be substituted.',
        },
      ],
    },
  ],
  currentPage: 1,
  totalPages: 1,
  pageSize: 10000,
};

/**
 * Student-only schools repository.
 * Uses legacy student-host endpoint shape when embedded in FC,
 * while preserving existing shared/staff school repository behavior.
 */
export function useGetStudentSchools(enabled = true): UseQueryResult<School[], DefaultError> {
  const apiClient = useApiClient();
  const path = getStudentHighschoolsPath();

  return useQuery({
    queryKey: [...queryKeys.schools.list(), 'student'],
    queryFn: async (): Promise<School[]> => {
      if (isDirectApiMode() && !isEmbeddedInStudentHost()) {
        return MOCK_SCHOOLS;
      }

      const response = await apiClient.get<School[]>(path);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Fetch the authenticated student's current school when the district school list is unavailable.
 *
 * @param enabled - Whether the query should run
 * @returns The current school with its identifier and display name
 */
export function useGetCurrentStudentSchool(enabled = true): UseQueryResult<School, DefaultError> {
  const apiClient = useApiClient();
  const path = getStudentCurrentHighschoolPath();

  return useQuery({
    queryKey: [...queryKeys.schools.list(), 'student', 'current'],
    queryFn: async (): Promise<School> => {
      const response = await apiClient.get<unknown>(path);
      return getCurrentStudentSchool(response.data);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Fetch plans of study available for a specific active school in student flow.
 *
 * @param activeSchoolId - Active school NID used by the endpoint query string
 * @param userClassYear - The class year of the user to be passed as a header
 * @param enabled - Whether the query should run
 * @returns List of plan-of-study summaries for the selected school
 */
export function useGetStudentPlansOfStudyByActiveSchool(
  activeSchoolId: string,
  userClassYear: string,
  enabled = true
): UseQueryResult<StudentSchoolPlanOfStudySummary[], DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.plansOfStudy.list({ source: 'student', activeSchoolId }),
    queryFn: async (): Promise<StudentSchoolPlanOfStudySummary[]> => {
      if (isDirectApiMode() && !isEmbeddedInStudentHost()) {
        return MOCK_STUDENT_PLANS_BY_ACTIVE_SCHOOL.data;
      }

      const path = getStudentPlansOfStudyByActiveSchoolPath(activeSchoolId);
      // API returns either a bare array or a paginated envelope { total, data: [] }
      const response = await apiClient.get<
        StudentSchoolPlanOfStudySummary[] | StudentSchoolPlansResponseEnvelope
      >(path, {
        headers: {
          userclassyear: userClassYear,
        },
      });

      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray((response.data as StudentSchoolPlansResponseEnvelope).data)
          ? (response.data as StudentSchoolPlansResponseEnvelope).data
          : [];

      return items
        .filter(
          (item): item is StudentSchoolPlanOfStudySummary =>
            typeof item?.id === 'string' && typeof item?.name === 'string'
        )
        .map((item) => ({
          id: item.id,
          name: item.name,
          description: typeof item.description === 'string' ? item.description : undefined,
          descriptionPlainText:
            typeof item.descriptionPlainText === 'string' ? item.descriptionPlainText : undefined,
          activeSchools: Array.isArray(item.activeSchools)
            ? item.activeSchools
                .map((school) => ({
                  id: typeof school?.id === 'string' ? school.id : undefined,
                  nid: typeof school?.nid === 'string' ? school.nid : undefined,
                  name: typeof school?.name === 'string' ? school.name : undefined,
                }))
                .filter((school) => typeof school.id === 'string' || typeof school.nid === 'string')
            : undefined,
          totalCredits: typeof item.totalCredits === 'number' ? item.totalCredits : undefined,
          credits: typeof item.credits === 'number' ? item.credits : undefined,
          requiredCredits:
            typeof item.requiredCredits === 'number' ? item.requiredCredits : undefined,
          requirements: Array.isArray(item.requirements) ? item.requirements : undefined,
        }));
    },
    enabled: enabled && Boolean(activeSchoolId),
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Fetch plans of study available across district/school scope in student flow.
 *
 * @param userClassYear - The class year of the user to be passed as a header
 * @param enabled - Whether the query should run
 * @returns List of plan-of-study summaries for the district/school scope
 */
export function useGetStudentPlansOfStudyByDistrictOrSchools(
  userClassYear: string,
  enabled = true
): UseQueryResult<StudentSchoolPlanOfStudySummary[], DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.plansOfStudy.list({
      source: 'student',
      scope: 'districtOrSchools',
    }),
    queryFn: async (): Promise<StudentSchoolPlanOfStudySummary[]> => {
      if (isDirectApiMode() && !isEmbeddedInStudentHost()) {
        return MOCK_STUDENT_PLANS_BY_ACTIVE_SCHOOL.data;
      }

      const path = getStudentPlansOfStudyByDistrictOrSchoolsPath();
      const response = await apiClient.post<
        StudentSchoolPlanOfStudySummary[] | StudentSchoolPlansResponseEnvelope
      >(path, undefined, {
        headers: {
          userclassyear: userClassYear,
        },
      });

      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray((response.data as StudentSchoolPlansResponseEnvelope).data)
          ? (response.data as StudentSchoolPlansResponseEnvelope).data
          : [];

      return items
        .filter(
          (item): item is StudentSchoolPlanOfStudySummary =>
            typeof item?.id === 'string' && typeof item?.name === 'string'
        )
        .map((item) => ({
          id: item.id,
          name: item.name,
          description: typeof item.description === 'string' ? item.description : undefined,
          descriptionPlainText:
            typeof item.descriptionPlainText === 'string' ? item.descriptionPlainText : undefined,
          activeSchools: Array.isArray(item.activeSchools)
            ? item.activeSchools
                .map((school) => ({
                  id: typeof school?.id === 'string' ? school.id : undefined,
                  nid: typeof school?.nid === 'string' ? school.nid : undefined,
                  name: typeof school?.name === 'string' ? school.name : undefined,
                }))
                .filter((school) => typeof school.id === 'string' || typeof school.nid === 'string')
            : undefined,
          totalCredits: typeof item.totalCredits === 'number' ? item.totalCredits : undefined,
          credits: typeof item.credits === 'number' ? item.credits : undefined,
          requiredCredits:
            typeof item.requiredCredits === 'number' ? item.requiredCredits : undefined,
          requirements: Array.isArray(item.requirements) ? item.requirements : undefined,
        }));
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
  });
}
